import { Component, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NzTableModule, NzTableSortFn } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { saveAs } from 'file-saver';
import { MediaDepotService } from '../../../../services/media-depot-service';
import { MediaDepotAsset } from '@mira-avant/mtlibs';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

export enum MediaPreviewType {
	AUDIO = 'audio',
	VIDEO = 'video',
	IMAGE = 'image',
	H5P = 'h5p'
}

@Component({
	selector: 'app-media-history-table',
	standalone: true,
	imports: [CommonModule, NzTableModule, NzButtonModule, NzTypographyModule, NzSpinModule, NzIconModule, NzTooltipModule],
	templateUrl: './media-history-table.html',
	styleUrl: './media-history-table.scss',
})
export class MediaHistoryTable {
	public assets = signal<MediaDepotAsset[]>([]);
	public loading = signal<boolean>(false);
	public downloading = signal<boolean>(false);
	private sub?: Subscription;
	private handle1: ReturnType<typeof setTimeout> | undefined;
	private handle2: ReturnType<typeof setTimeout> | undefined;
	public MediaPreviewType = MediaPreviewType;
	public pageIndex = signal(1);
	public assetDownloading = signal<MediaDepotAsset | null>(null);

	constructor(
		private mediaDepotService: MediaDepotService,
		private message: NzMessageService
	) {
		this.sub = this.mediaDepotService.assetsBs$.subscribe((assets: MediaDepotAsset[]) => {
			this.assets.set(assets);
			this.pageIndex.set(1);
		});
	}

	public sortByCreatedDesc: NzTableSortFn<MediaDepotAsset> = (a: MediaDepotAsset, b: MediaDepotAsset) => {
		return new Date(b.created).getTime() - new Date(a.created).getTime();
	};

	public copyToClipboard(url: string) {
		navigator.clipboard
			.writeText(url)
			.then(() => {
				this.message.success('Copied URL to clipboard');
			})
			.catch(err => {
				this.message.error('Failed to copy URL to clipboard');
			});
	}

	getPreviewType(url: string): MediaPreviewType | undefined {
		if (!url) {
			return undefined;
		}

		const lower = url.toLowerCase();

		if (
			lower.endsWith('.mp4') ||
			lower.endsWith('.webm') ||
			lower.endsWith('.mov') ||
			lower.endsWith('.ogv') ||
			lower.endsWith('.avi') ||
			lower.endsWith('.mkv')
		) {
			return MediaPreviewType.VIDEO;
		} else if (lower.endsWith('.mp3') || lower.endsWith('.wav') || lower.endsWith('.ogg')) {
			return MediaPreviewType.AUDIO;
		} else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.gif')) {
			return MediaPreviewType.IMAGE;
		} else if (lower.endsWith('h5p.json')) {
			return MediaPreviewType.H5P;
		} else {
			return undefined;
		}
	}

	/**
	 * This will download the URL it will not 'navigate' to the URL
	 *
	 * it will use the file-saver NPM.
	 *
	 * @public
	 * @param {MediaDepotAsset} asset
	 */
	public downloadFile(asset: MediaDepotAsset) {
		this.downloading.set(true);
		this.assetDownloading.set(asset);

		if (this.handle1) {
			clearTimeout(this.handle1);
		}

		if (this.handle2) {
			clearTimeout(this.handle2);
		}

		this.handle1 = setTimeout(() => {
			saveAs(asset.url, asset.masked_filename);

			this.handle2 = setTimeout(() => {
				this.downloading.set(false);
				this.assetDownloading.set(null);
			}, 250);
		}, 250);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();

		if (this.handle1) {
			clearTimeout(this.handle1);
		}

		if (this.handle2) {
			clearTimeout(this.handle2);
		}
	}
}
